import React, { useState, useRef, useEffect, useMemo } from "react";
import { CloudUpload, Download, FileText, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ReactComponent as Eye } from "../../../../icons/View_eye.svg";
import { ReactComponent as Delete } from "../../../../icons/View_dlt.svg";
import ModHeader from "../Mod/ModTable/ModHeader";
import { useSelector } from "react-redux";
import {
  ModBulkUploadFile,
  fetchAvailabilityDetail,
  getAvailabilityDetailSheet,
  getDemandForecastDaywiseSheet,
  getAvailabilityDaywiseSheet,
  getBankingDaywiseSheet,
  getOverhaulingDaywiseSheet,
  getFinalAvailabilityDaywiseSheet,
  getShortageSurplusDaywiseSheet,
  getAvailabilitySummarySheet,
  get15DaysReportBlockwiseSheet,
  get15DaysReportHourlySheet,
  getMonthlyReportBlockwiseSheet,
  getMonthlyReportHourlySheet,
  getOnlyShortageSurplusHourlySheet,
} from "../../Utility/RestApi";
import { DaywiseSheetTable } from "./DaywiseSheetTable";
import { AvailabilitySummaryTable } from "./AvailabilitySummaryTable";
import { PeriodReportTable } from "./PeriodReportTable";
import { ShortageSurplusHourlyTable } from "./ShortageSurplusHourlyTable";
import Tooltip from "../../../../components/tooltip";
import { useMeritEngineNavigation } from "../useMeritEngineNavigation";
import MainHeader from "../DateSession";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import { SearchBox } from "../../../../components/Search";
import ToggleTabs from "../Mod/ModTable/ChartTableToggle";
import { EmptyState } from "../../../../components/recharts/ReMixCharts/components/EmptyState";

// Helper function to format the date as DD-MMM-YYYY
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  const formattedDate = new Intl.DateTimeFormat("en-GB", options).format(date);
  return formattedDate.replace(/ /g, "-");
};

type UploadedFile = {
  type: string;
  date: string;
  file_url: string;
  file: File;
  id: number;
  isTemplate: boolean;
};

interface AvailabilityRow {
  date: string;
  time_block: number;
  time_slot: string;
  category: "Must_Run" | "MOD";
  ownership: Ownership;
  source: string;
  generator_name: string;
  value: number | null; /* null = generator has no data for this slot */
}

type Mode = "Must_Run" | "MOD";
type Ownership = "ISGS" | "GNA" | "SGS" | "REMC";
type GenType =
  | "Hydro"
  | "Solar"
  | "Thermal"
  | "Biomass"
  | "STOA"
  | "BESS"
  | "Wind";

interface GeneratorDef {
  id: string;
  displayName: string;
  shortName: string;
  ownership: Ownership;
  genType: GenType;
  mode: Mode;
}

const OWNERSHIP_ORDER: Ownership[] = ["ISGS", "GNA", "SGS", "REMC"];
const GENTYPE_ORDER: GenType[] = [
  "Hydro",
  "Solar",
  "Thermal",
  "Biomass",
  "STOA",
  "BESS",
  "Wind",
];
const SUMMARY_COLS: string[] = [];

const TIME_BLOCKS = Array.from({ length: 96 }, (_, i) => {
  const startMin = i * 15;
  const endMinTotal = (i + 1) * 15;
  const sh = Math.floor(startMin / 60)
    .toString()
    .padStart(2, "0");
  const sm = (startMin % 60).toString().padStart(2, "0");
  const eh = Math.floor((endMinTotal % 1440) / 60)
    .toString()
    .padStart(2, "0");
  const em = (endMinTotal % 60).toString().padStart(2, "0");
  return { block: i + 1, slot: `${sh}:${sm}-${eh}:${em}` };
});

function groupGens(gens: GeneratorDef[]) {
  return OWNERSHIP_ORDER.map((ownership) => {
    const ownGens = gens.filter((g) => g.ownership === ownership);
    if (ownGens.length === 0) return null;
    const genTypes = GENTYPE_ORDER.map((gt) => ({
      genType: gt as GenType,
      gens: ownGens.filter((g) => g.genType === gt),
    })).filter((x) => x.gens.length > 0);
    return { ownership, genTypes, count: ownGens.length };
  }).filter(
    (
      x,
    ): x is {
      ownership: Ownership;
      genTypes: { genType: GenType; gens: GeneratorDef[] }[];
      count: number;
    } => x !== null,
  );
}

const BaseFileRegexPatterns: { [key: string]: RegExp } = {
  AVT: /^AVT \d{2}\.\d{2}\.\d{4}\s*\.xlsx(?: - .*\.csv)?$/i,
  "Day Ahead Schedule for MTOA":
    /^Day Ahead Schedule for MTOA\((Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2}\)( \(\d+\))?\.xlsx$/,
  "Declared Capacity of GKEL":
    /^Declared Capacity of GKEL for\s+\d{2}\.\d{2}\.\d{4}\s*\.xlsx$/,
  "IEX DOR GRIDCO": /^IEX\d{6}DOR_GDC0001_OR0_GRIDCO\.xls$/,
  "JSW Energy Utkal Schedule":
    /^JSW Energy Utkal Schedule & GRIDCO allocation for \d{2}\.\d{2}\.\d{2}\.xlsx$/,
  "NetSchdReportBreakup ISGS": /^cspdcl_isgs_template\.(csv|xlsx)$/i,
  "NetSchdReportBreakup OA_GNA": /^cspdcl_oa_gna_template\.(csv|xlsx)$/i,
  "NetSchdReportBreakup OA_REMC": /^cspdcl_oa_remc_template\.(csv|xlsx)$/i,
  "OPGC STAGE-2 DC": /^OPGC STAGE-2 DC\s+\d{2}\.\d{2}\.\d{4}\s+-\s+R\d+\.xlsx$/,
  "RTM_IEX DOR GRIDCO": /^RTM_IEX\d{6}DOR_GDC0001_OR0_GRIDCO(\s?\(1\))?\.XLS$/,
  "SLDC LDS":
    /^SLDC LDS \d{2} (Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?) \d{4} \(Rev-\d+\)\.xlsx$/i,
  "NBVL(IPP) DAY AHEAD SCHEDULE":
    /^NBVLIPP_DAY_AHEAD_SCHEDULE_TO_GRIDCO_FOR_(\d{2}\.\d{2}\.\d{4})\.xlsx$/,
  jipl_dc_pdf: /^JIPL DC for DD \d{2}\.\d{2}\.\d{4}\.pdf$/,
  sldc_report: /^SLDC_Report__(\d{2})(\d{2})(\d{4})\.xlsx(?: - .*\.csv)?$/i,
  Intra_State_RE: /^Intra_State_RE\.xlsx$/,
  CGP_Injection: /^CGP_Injection\.xlsx$/,
  Buffer: /^Buffer\.xlsx$/,
  Vc: /^cspdcl_vc_template\.(csv|xlsx)$/i,
  Banking: /^Banking\.xlsx$/,
  "Short-Term": /^Short-Term\.xlsx$/,
};

const FileRegexPatterns: { [key: string]: RegExp } = {
  ...BaseFileRegexPatterns,
  JIPL_TEMPLATE: /^JIPL\.xlsx$/,
  JSWEUL_TEMPLATE: /^JSWEUL\.xlsx$/,
  "Maa Durga_TEMPLATE": /^Maa Durga\.xlsx$/,
  NBVL_TEMPLATE: /^NBVL\.xlsx$/,
  "NET SCHEDULED_TEMPLATE": /^NET SCHEDULED\.xlsx$/,
  OA_GNA_TEMPLATE: /^OA_GNA\.xlsx$/,
  OA_REMC_TEMPLATE: /^OA_REMC\.xlsx$/,
  "OPGC-II Unit-3&4_TEMPLATE": /^OPGC-II Unit-3&4\.xlsx$/,
  "STATE HYDRO_TEMPLATE": /^STATE HYDRO\.xlsx$/,
  Vedanta_TEMPLATE: /^Vedanta\.xlsx$/,
  GMR_TEMPLATE: /^GMR\.xlsx$/,
  IBTPS_TEMPLATE: /^IBTPS\.xlsx$/,
};

const RegexToFileTypeMap: { [key: string]: string } = {
  AVT: "OPGC-1 (IBTPS Unit -1 & 2)",
  "OPGC STAGE-2 DC": "OPGC-2 (IBTPS Unit -3 & 4)",
  "SLDC LDS": "Vedanta",
  "Declared Capacity of GKEL": "GMR",
  "JSW Energy Utkal Schedule": "JSWEUL",
  "NBVL(IPP) DAY AHEAD SCHEDULE": "NBVL",
  "NetSchdReportBreakup ISGS": "Net Schedule ISGS",
  "NetSchdReportBreakup OA_GNA": "Net Schedule OA GNA",
  "NetSchdReportBreakup OA_REMC": "Net Schedule OA REMC",
  "RTM_IEX DOR GRIDCO": "RTM day current",
  "IEX DOR GRIDCO": "DAM day current",
  "Day Ahead Schedule for MTOA": "Maa Durga",
  jipl_dc_pdf: "JIPL",
  sldc_report: "State Hydro Generation",
  Intra_State_RE: "Intra State RE",
  CGP_Injection: "CGP Injection",
  Buffer: "Buffer",
  Vc: "VC for Generators (Optional)",
  Banking: "Banking",
  "Short-Term": "Short Term Buy/Sell",
  JIPL_TEMPLATE: "JIPL Template",
  JSWEUL_TEMPLATE: "JSWEUL Template",
  "Maa Durga_TEMPLATE": "Maa Durga Template",
  NBVL_TEMPLATE: "NBVL Template",
  "NET SCHEDULED_TEMPLATE": "Net Schedule ISGS Template",
  OA_GNA_TEMPLATE: "Net Schedule OA GNA Template",
  OA_REMC_TEMPLATE: "Net Schedule OA REMC Template",
  "OPGC-II Unit-3&4_TEMPLATE": "OPGC-2 (IBTPS Unit -3 & 4) Template",
  "STATE HYDRO_TEMPLATE": "State Hydro Generation Template",
  Vedanta_TEMPLATE: "Vedanta Template",
  GMR_TEMPLATE: "GMR Template",
  IBTPS_TEMPLATE: "OPGC-1 (IBTPS Unit -1 & 2) Template",
};

const TemplateToStandardMap: { [key: string]: string } = {
  "JIPL Template": "JIPL",
  "JSWEUL Template": "JSWEUL",
  "Maa Durga Template": "Maa Durga",
  "NBVL Template": "NBVL",
  "Net Schedule ISGS Template": "Net Schedule ISGS",
  "Net Schedule OA GNA Template": "Net Schedule OA GNA",
  "Net Schedule OA REMC Template": "Net Schedule OA REMC",
  "OPGC-2 (IBTPS Unit -3 & 4) Template": "OPGC-2 (IBTPS Unit -3 & 4)",
  "State Hydro Generation Template": "State Hydro Generation",
  "Vedanta Template": "Vedanta",
  "GMR Template": "GMR",
  "OPGC-1 (IBTPS Unit -1 & 2) Template": "OPGC-1 (IBTPS Unit -1 & 2)",
};

type FileRowProps = {
  fileName: string;
  uploadedFile: File | null;
  fileError: string | null;
  isTemplate: boolean;
  onFileChange: (
    fileName: string,
    file: File | null,
    isTemplate: boolean,
  ) => void;
  onFileError: (fileName: string, error: string | null) => void;
};

const getValidPatternsForFileType = (fileName: string): RegExp[] => {
  const patterns: RegExp[] = [];

  Object.entries(RegexToFileTypeMap).forEach(([regexKey, mappedType]) => {
    if (mappedType === fileName) {
      const pattern = FileRegexPatterns[regexKey];
      if (pattern) {
        patterns.push(pattern);
      }
    }
  });

  const templateKey = Object.keys(TemplateToStandardMap).find(
    (key) => TemplateToStandardMap[key] === fileName,
  );
  if (templateKey) {
    const templateRegexKey = Object.keys(RegexToFileTypeMap).find(
      (key) => RegexToFileTypeMap[key] === templateKey,
    );
    if (templateRegexKey && FileRegexPatterns[templateRegexKey]) {
      patterns.push(FileRegexPatterns[templateRegexKey]);
    }
  }

  return patterns;
};

const FileRow: React.FC<FileRowProps> = ({
  fileName,
  uploadedFile,
  fileError,
  isTemplate,
  onFileChange,
  onFileError,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (
        fileExtension !== "xlsx" &&
        fileExtension !== "csv" &&
        fileExtension !== "pdf"
      ) {
        const errorMsg =
          "Invalid file type. Only .xlsx and .csv files are allowed.";
        setError(`Error uploading "${fileName}":\n${errorMsg}`);
        onFileError(fileName, errorMsg);
        onFileChange(fileName, null, false);
        return;
      }

      const validPatterns = getValidPatternsForFileType(fileName);
      let detectedIsTemplate = false;

      if (validPatterns.length > 0) {
        const isValidFileName = validPatterns.some((pattern) =>
          pattern.test(file.name),
        );

        if (!isValidFileName) {
          const errorMsg = `File name does not match required pattern for ${fileName}.`;
          setError(`Error uploading "${fileName}":\n${errorMsg}`);
          onFileError(fileName, errorMsg);
          onFileChange(fileName, null, false);
          return;
        }

        const templateKey = Object.keys(TemplateToStandardMap).find(
          (key) => TemplateToStandardMap[key] === fileName,
        );
        if (templateKey) {
          const templateRegexKey = Object.keys(RegexToFileTypeMap).find(
            (key) => RegexToFileTypeMap[key] === templateKey,
          );
          if (
            templateRegexKey &&
            FileRegexPatterns[templateRegexKey] &&
            FileRegexPatterns[templateRegexKey].test(file.name)
          ) {
            detectedIsTemplate = true;
          }
        }
      }

      onFileError(fileName, null);
      onFileChange(fileName, file, detectedIsTemplate);
    } else {
      onFileChange(fileName, null, false);
      onFileError(fileName, null);
    }
  };

  const handleViewFile = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      window.open(url, "_blank");
    }
  };

  const handleDeleteFile = () => {
    onFileChange(fileName, null, false);
    onFileError(fileName, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isReady = uploadedFile && !fileError;
  const isError = !uploadedFile && fileError;

  const fileConfig: Record<string, string[]> = {
    "VC for Generators (Optional)": [],
    "OPGC-2 (IBTPS Unit -3 & 4)": ["OPGC-II Unit-3&4"],
    "OPGC-1 (IBTPS Unit -1 & 2)": ["IBTPS"],
    Vedanta: ["Vedanta"],
    GMR: ["GMR"],
    JIPL: ["JIPL"],
    JSWEUL: ["JSWEUL"],
    NBVL: ["NBVL"],
    "Maa Durga": ["Maa Durga"],
    "Net Schedule OA REMC": [
      "AP41PL_BHDL",
      "AP43PL_BKN",
      "AWEK1L",
      "AWEK1L",
      "DADRI_SOLAR",
      "GADAG_GreenInfra_W",
      "GIWEL_SECI_II_RE",
      "GSECL_SOLAR",
      "OKWPL_RE",
      "RWE_AP2_SECI_III",
      "Tuticorin_BETAMWIND",
      "GPCL_SOLAR_10",
      "IRON_TRINGLE_10_MW_SOLAR",
      "XXPPL_FTG3",
      "ENERSON_SOLAR_10MW",
    ],
    "Net Schedule OA GNA": ["FARIDABAD_SOLAR", "RAJ_SOLAR", "RAJ_SOLAR"],
    "State Hydro Generation": [
      "Burla",
      "Chiplima",
      "Balimela",
      "Rengali",
      "U Kolab",
      "U Indravati",
      "Machkund O/D",
    ],
    "Net Schedule ISGS": [
      "BARH|TO_ODISHA",
      "BARH_I|TO_ODISHA",
      "CHUKHA|TO_ODISHA",
      "Darlipali_NTPC|TO_ODISHA",
      "FSTPP_III|TO_ODISHA",
      "FSTPP_III|TO_ODISHA_Solar_Beneficiary",
      "FSTPP_I_II|TO_ODISHA",
      "FSTPP_I_II|TO_ODISHA_Solar_Beneficiary",
      "KGSU1AND2|TO_ODISHA",
      "KGSU3AND4|TO_ODISHA",
      "KHSTPP_I|TO_ODISHA",
      "KHSTPP_I|TO_ODISHA_Solar_Beneficiary",
      "KHSTPP_II|TO_ODISHA",
      "KHSTPP_II|TO_ODISHA_Solar_Beneficiary",
      "KKNP|TO_ODISHA",
      "KKNPPU2|TO_ODISHA",
      "KUDGI|TO_ODISHA",
      "KURICHU|TO_ODISHA",
      "MANGDECHU|TO_ODISHA",
      "MAPS|TO_ODISHA",
      "MTPS_II|TO_ODISHA",
      "NLCEXP|TO_ODISHA",
      "NLCIIST1|TO_ODISHA",
      "NLCIIST2|TO_ODISHA",
      "NLCTS2EXP|TO_ODISHA",
      "NNTPP|TO_ODISHA",
      "NPGC|TO_ODISHA",
      "NTPL|TO_ODISHA",
      "North_Karanpura_STPS|TO_ODISHA",
      "RANGIT|TO_ODISHA",
      "RSTPSU1TO6|TO_ODISHA",
      "RSTPSU7|TO_ODISHA",
      "SIMHST1|TO_ODISHA",
      "SIMHST2|TO_ODISHA",
      "TALA|TO_ODISHA",
      "TALST2|TO_ODISHA",
      "TSTPP_I|TO_ODISHA",
      "TSTPP_I|TO_ODISHA_Solar_Beneficiary",
      "Teesta_V|TO_ODISHA",
      "TelanganaSTPP|TO_ODISHA",
      "VALLURNTECL|TO_ODISHA",
    ],
    "Intra State RE": ["SOLAR", "WIND", "BIOMASS", "SHEP"],
    "CGP Injection": ["CGP_INJECTION"],
    Buffer: ["buffer"],
    Banking: ["Total"],
    "Short Term Buy/Sell": ["Total"],
  };

  function getCustomFileName(fileName: string) {
    switch (fileName) {
      case "VC for Generators (Optional)":
        return `VC_all_generators.xlsx`;
      case "Net Schedule ISGS":
        return `NET SCHEDULED.xlsx`;
      case "OPGC-2 (IBTPS Unit -3 & 4)":
        return `OPGC-II Unit-3&4.xlsx`;
      case "OPGC-1 (IBTPS Unit -1 & 2)":
        return `IBTPS.xlsx`;
      case "Net Schedule OA GNA":
        return `OA_GNA.xlsx`;
      case "Net Schedule OA REMC":
        return `OA_REMC.xlsx`;
      case "GMR":
        return `GMR.xlsx`;
      case "JIPL":
        return `JIPL.xlsx`;
      case "JSWEUL":
        return `JSWEUL.xlsx`;
      case "NBVL":
        return `NBVL.xlsx`;
      case "Maa Durga":
        return `Maa Durga.xlsx`;
      case "State Hydro Generation":
        return `STATE HYDRO.xlsx`;
      case "Intra State RE":
        return `Intra_State_RE.xlsx`;
      case "CGP Injection":
        return `CGP_Injection.xlsx`;
      case "Buffer":
        return `Buffer.xlsx`;
      case "Short Term Buy/Sell":
        return `Short-Term.xlsx`;
      default:
        return `${fileName}.xlsx`;
    }
  }
  const cspdclTemplateFileMap: Record<string, string> = {
    "Net Schedule ISGS": "cspdcl_isgs_template.csv",
    "Net Schedule OA GNA": "cspdcl_oa_gna_template.csv",
    "Net Schedule OA REMC": "cspdcl_oa_remc_template.csv",
    "VC for Generators (Optional)": "cspdcl_vc_template.csv",
  };

  const handleDownloadTemplate = async (fileName: string) => {
    if (!fileName) return;

    const templateFile = cspdclTemplateFileMap[fileName];
    if (templateFile) {
      const link = document.createElement("a");
      link.href = `${process.env.PUBLIC_URL}/${templateFile}`;
      link.download = templateFile;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    const columns = fileConfig[fileName];
    if (!columns) {
      console.error("❌ Unknown fileName:", fileName);
      return;
    }

    const timeSlots = Array.from({ length: 96 }, (_, i) => i + 1);

    // Rest of your existing code for other templates
    const data = timeSlots.map((slot) => {
      const row: Record<string, any> = { time_block: slot };
      columns.forEach((col) => {
        row[col] = "";
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(data, {
      header: ["time_block", ...columns],
    });
    XLSX.utils.sheet_add_aoa(worksheet, [["time_block", ...columns]], {
      origin: "A1",
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, getCustomFileName(fileName));
  };

  return (
    <div className="flex items-center justify-between py-2 px-1 border mb-3 border-[#F3F5F8] last:border-b-0">
      <div className="flex items-center gap-3">
        <FileText size={20} style={{ color: "#8C8A96" }} />
        <span
          className={`text-sm font-medium ${fileName === "VC for Generators (Optional)" ? "text-[#F1935C]" : "text-[#525062]"}`}
        >
          {fileName}
        </span>
      </div>

      {isReady ? (
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#525062" }}>
            {formatDate(new Date().toISOString().split("T")[0])}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleViewFile}
              className="text-[#F1935C] hover:opacity-80 transition-opacity p-1 rounded"
            >
              <Eye />
            </button>
            <button
              className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
              style={{ backgroundColor: "#00BF6F", color: "#fff" }}
            >
              <CheckCircle size={14} /> <span>Ready</span>
            </button>

            <button
              onClick={handleDeleteFile}
              className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
            >
              <Delete className="w-8 h-8" />
            </button>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#525062" }}>
            {formatDate(new Date().toISOString().split("T")[0])}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
              style={{ backgroundColor: "#FDE6E8", color: "#B32130" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x-circle"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m15 9-6 6" />
                <path d="m9 9 6 6" />
              </svg>{" "}
              <span>Error</span>
            </button>
            <button
              onClick={handleDeleteFile}
              className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
            >
              <Delete className="w-8 h-8" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDownloadTemplate(fileName)}
            className="flex items-center justify-center gap-2 w-[110px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-colors  bg-[#FFFAF6] text-[#F1935C]"
          >
            <Download className="w-4 h-4" />
            <span>Template</span>
          </button>
          <button
            onClick={handleUploadClick}
            className="flex items-center justify-center gap-2 w-[90px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
            style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
          >
            <CloudUpload size={14} /> Upload
          </button>
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".xlsx,.csv,.pdf"
      />
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </div>
  );
};

/* ── Colour system: platform teal #34656D (matches reference table) ─────── */
const GENTYPE_CFG: Partial<
  Record<GenType, { icon: string; bg: string; text: string }>
> = {
  Thermal: { icon: "🔥", bg: "#EDF4F5", text: "#34656D" },
  Hydro: { icon: "💧", bg: "#EDF4F5", text: "#34656D" },
  Solar: { icon: "☀️", bg: "#EDF4F5", text: "#34656D" },
  Biomass: { icon: "🌿", bg: "#EDF4F5", text: "#34656D" },
  BESS: { icon: "⚡", bg: "#EDF4F5", text: "#34656D" },
  STOA: { icon: "📊", bg: "#EDF4F5", text: "#34656D" },
  Wind: { icon: "🌬️", bg: "#EDF4F5", text: "#34656D" },
};

const OWNERSHIP_CFG: Partial<Record<Ownership, { bg: string; text: string }>> =
  {
    ISGS: { bg: "#EDF4F5", text: "#34656D" },
    GNA: { bg: "#EDF4F5", text: "#34656D" },
    SGS: { bg: "#EDF4F5", text: "#34656D" },
    REMC: { bg: "#EDF4F5", text: "#34656D" },
  };

const AvailabilityTable: React.FC<{
  selectedMode: Mode;
  selectedOwnerships: string[];
  selectedGenTypes: string[];
  selectedGeneratorIds: string[];
  generators: GeneratorDef[];
  dataMap: Map<string, Map<number, number | null>>;
  loading: boolean;
}> = ({
  selectedMode,
  selectedOwnerships,
  selectedGenTypes,
  selectedGeneratorIds,
  generators,
  dataMap,
  loading,
}) => {
  const [hoveredGenId, setHoveredGenId] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  /* ── Filter generators ─────────────────────────────────────────── */
  const filter = (g: GeneratorDef) =>
    g.mode === selectedMode &&
    (selectedOwnerships.length === 0 ||
      selectedOwnerships.includes(g.ownership)) &&
    (selectedGenTypes.length === 0 || selectedGenTypes.includes(g.genType)) &&
    (selectedGeneratorIds.length === 0 || selectedGeneratorIds.includes(g.id));

  const filteredGens = [...generators.filter(filter)].sort((a, b) => {
    const oi =
      OWNERSHIP_ORDER.indexOf(a.ownership) -
      OWNERSHIP_ORDER.indexOf(b.ownership);
    if (oi !== 0) return oi;
    return GENTYPE_ORDER.indexOf(a.genType) - GENTYPE_ORDER.indexOf(b.genType);
  });

  /* Only render rows for time blocks that actually exist in the API response.
     When dataMap is empty (no session / no data), this is empty and the
     table body stays blank — no phantom rows from the hardcoded TIME_BLOCKS. */
  const availableBlocks = useMemo(() => {
    const blockNums = new Set<number>();
    dataMap.forEach((blockMap) => blockMap.forEach((_, b) => blockNums.add(b)));
    return TIME_BLOCKS.filter(({ block }) => blockNums.has(block));
  }, [dataMap]);

  const grouped = groupGens(filteredGens);
  const uniqueTypes = [...new Set(filteredGens.map((g) => g.genType))];
  const totalInMode = generators.filter((g) => g.mode === selectedMode).length;

  /* ── Style constants ────────────────────────────────────────────── */
  const TEAL = "#34656D";
  const BORDER = "1px solid #E2E8F0";
  /* Opaque solid header background via backgroundImage to bypass the
     global  th { background-color: #fff8f4 !important }  rule.
     Using an opaque colour (not rgba) is the fix for the overlap bug. */
  const HDR_BG = "linear-gradient(#EDF4F5,#EDF4F5)";
  const TS_W = 130;

  const cell: React.CSSProperties = {
    border: BORDER,
    textAlign: "center",
    verticalAlign: "middle",
    padding: "0 10px",
    fontSize: 12,
  };

  /* Time Slot header — sticky top-left corner
     MUST use an opaque backgroundImage so scrolled content doesn't
     bleed through when scrolling horizontally.                        */
  const stickyTsHeader: React.CSSProperties = {
    ...cell,
    position: "sticky",
    top: 0,
    left: 0,
    zIndex: 22,
    backgroundImage: HDR_BG /* fully opaque #EDF4F5 */,
    color: TEAL,
    fontWeight: 700,
    fontSize: 12,
    height: 44,
    minWidth: TS_W,
    whiteSpace: "nowrap",
    borderRight: `2px solid ${TEAL}`,
    boxShadow: "3px 0 6px rgba(52,101,109,0.12)",
    borderBottom: "2px solid #CAD5E2",
  };

  /* Generator name header cells — sticky top only (not left) */
  const genHeaderStyle = (isCol: boolean): React.CSSProperties => ({
    ...cell,
    position: "sticky",
    top: 0,
    zIndex: 10,
    /* Solid opaque background — crucial to prevent overlap when scrolling */
    backgroundImage: isCol ? `linear-gradient(${TEAL},${TEAL})` : HDR_BG,
    color: isCol ? "#fff" : TEAL,
    fontWeight: 600,
    fontSize: 11,
    minWidth: 90,
    maxWidth: 140,
    height: 44,
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.35,
    padding: "6px 8px",
    cursor: "default",
    transition: "color 0.15s",
    borderBottom: "2px solid #CAD5E2",
  });

  /* Sticky Time Slot — body cells: plain white (opaque) */
  const stickyTsTd = (isRow: boolean): React.CSSProperties => ({
    ...cell,
    position: "sticky",
    left: 0,
    zIndex: 5,
    backgroundColor: isRow ? "#EDF4F5" : "#FFFFFF" /* fully opaque */,
    color: TEAL,
    fontWeight: 500,
    height: 34,
    minWidth: TS_W,
    whiteSpace: "nowrap",
    borderRight: `2px solid ${TEAL}`,
    boxShadow: "3px 0 6px rgba(52,101,109,0.08)",
    transition: "background-color 0.12s",
  });

  /* ── Loading skeleton ─────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="rounded-xl p-4 h-24 animate-pulse"
              style={{
                backgroundColor: "#EDF4F5",
                border: "1px solid #E2E8F0",
              }}
            />
          ))}
        </div>
        <div
          className="rounded-xl h-64 animate-pulse flex items-center justify-center text-sm"
          style={{
            backgroundColor: "#EDF4F5",
            border: "1px solid #E2E8F0",
            color: TEAL,
          }}
        >
          Loading availability data…
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      {/* ── Filter Summary Bar ────────────────────────────────────── */}
      <div
        className="flex items-center gap-2 px-4 py-2 rounded-xl flex-wrap bg-white"
        style={{ border: "1px solid #E2E8F0" }}
      >
        <span
          className="text-[10px] font-bold uppercase tracking-widest mr-1"
          style={{ color: "#8C8A96" }}
        >
          Active Filters
        </span>

        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
          style={{ backgroundColor: "#EDF4F5", color: TEAL }}
        >
          {selectedMode === "Must_Run" ? "⚙️ Must Run" : "📡 MOD"}
        </span>

        {selectedOwnerships.map((o) => (
          <span
            key={o}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
            style={{ backgroundColor: "#EDF4F5", color: TEAL }}
          >
            🏢 {o}
          </span>
        ))}

        {selectedGenTypes.map((t) => {
          const cfg = GENTYPE_CFG[t as GenType];
          return (
            <span
              key={t}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: "#EDF4F5", color: TEAL }}
            >
              {cfg?.icon ?? "⚡"} {t}
            </span>
          );
        })}

        {filteredGens.length === 0 ? (
          <span className="font-semibold text-xs ml-2" style={{ color: TEAL }}>
            {generators.length === 0
              ? "No data — select a date with an active session"
              : "No generators match — clear filters"}
          </span>
        ) : (
          <span className="ml-auto text-xs" style={{ color: "#8C8A96" }}>
            {filteredGens.length} generator
            {filteredGens.length !== 1 ? "s" : ""} · 96 time slots
          </span>
        )}
      </div>

      {/* ── Pivot table: Time Slot × Generator Name ───────────────── */}
      {/* ── No-data guard: show EmptyState instead of an empty table ─── */}
      {availableBlocks.length === 0 ? (
        <div
          className="rounded-xl flex-1 min-h-0 relative bg-white"
          style={{ border: "1px solid #E2E8F0", minHeight: 320 }}
        >
          <EmptyState />
        </div>
      ) : (
        <div
          className="rounded-xl overflow-hidden bg-white flex-1 min-h-0 flex flex-col"
          style={{
            border: "1px solid #E2E8F0",
            boxShadow: "0 2px 12px rgba(52,101,109,0.08)",
          }}
        >
          <div
            style={{
              overflowX: "auto",
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
            }}
          >
            <table
              style={{
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: 12,
                minWidth: "100%",
              }}
            >
              {/* ── Single header row ───────────────────────────────── */}
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  boxShadow: "0 2px 0 0 #CAD5E2",
                }}
              >
                <tr>
                  {/* Time Slot — sticky first column, opaque background */}
                  <th style={stickyTsHeader}>Time Slot</th>

                  {/* Generator name columns */}
                  {filteredGens.map((gen) => {
                    const cfg = GENTYPE_CFG[gen.genType];
                    const isCol = hoveredGenId === gen.id;
                    return (
                      <th
                        key={gen.id}
                        title={`${gen.displayName}\nOwnership: ${gen.ownership}\nType: ${gen.genType}`}
                        onMouseEnter={() => setHoveredGenId(gen.id)}
                        onMouseLeave={() => setHoveredGenId(null)}
                        style={genHeaderStyle(isCol)}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <span style={{ fontSize: 13 }}>
                            {cfg?.icon ?? ""}
                          </span>
                          <span>{gen.displayName}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* ── Body: one row per time slot ─────────────────────── */}
              <tbody>
                {availableBlocks.map(({ block, slot }, rowIdx) => {
                  const isRow = hoveredRow === rowIdx;
                  const rowBg = isRow
                    ? "#EDF4F5"
                    : rowIdx % 2 === 0
                      ? "#FFFFFF"
                      : "#F7FAFB";

                  return (
                    <tr
                      key={block}
                      onMouseEnter={() => setHoveredRow(rowIdx)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{
                        backgroundColor: rowBg,
                        transition: "background-color 0.12s",
                      }}
                    >
                      {/* Sticky Time Slot — opaque white/teal */}
                      <td style={stickyTsTd(isRow)}>{slot}</td>

                      {/* Data cells */}
                      {filteredGens.map((gen) => {
                        const isCol = hoveredGenId === gen.id;
                        const raw = dataMap.get(gen.id)?.get(block);
                        const hasVal = raw !== undefined;
                        const display = hasVal
                          ? raw === 0
                            ? "0"
                            : raw!.toFixed(2)
                          : "–";

                        return (
                          <td
                            key={gen.id}
                            onMouseEnter={() => setHoveredGenId(gen.id)}
                            onMouseLeave={() => setHoveredGenId(null)}
                            style={{
                              ...cell,
                              minWidth: 80,
                              height: 34,
                              backgroundColor:
                                isCol && isRow
                                  ? "rgba(52,101,109,0.15)"
                                  : isCol
                                    ? "rgba(52,101,109,0.06)"
                                    : "transparent",
                              color: !hasVal
                                ? "#D4D4D8"
                                : raw === 0
                                  ? "#94A3B8"
                                  : "#374151",
                              fontWeight: hasVal && raw! > 0 ? 500 : 400,
                              transition: "background-color 0.12s",
                            }}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export const SHEETS = [
  { id: "Availability_Detail", label: "Availability Detail" },
  { id: "Demand_Forecast_Daywise", label: "Demand Forecast (Day-wise)" },
  { id: "Availability_Summary", label: "Availability Summary" },
  { id: "Availability_Daywise", label: "Availability (Day-wise)" },
  { id: "Banking_Daywise", label: "Banking (Day-wise)" },
  { id: "Overhauling_Daywise", label: "Overhauling (Day-wise)" },
  { id: "Final_Availability_Daywise", label: "Final Availability (Day-wise)" },
  { id: "Shortage_Surplus_Daywise", label: "Shortage / Surplus (Day-wise)" },
  { id: "15Days_Report-Blockwise", label: "15-Day Report · Block-wise" },
  { id: "15Days_Report-Hourly", label: "15-Day Report · Hourly" },
  { id: "Monthly_Report-Blockwise", label: "Monthly Report · Block-wise" },
  { id: "Monthly_Report-Hourly", label: "Monthly Report · Hourly" },
  { id: "Only_Shortage_Surplus_Hourly", label: "Shortage / Surplus · Hourly" },
];

export default function GenerationTab() {
  const [error, setError] = useState<string | null>(null);
  const [selectedSheet, setSelectedSheet] = useState(SHEETS[0].id);
  const preSelectedDate = useSelector(
    (state: any) => state.modCSPDCLDate.selectedDate,
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modCSPDCLSession.modSession,
  );
  const [loading, setLoading] = useState(false);
  const [currentSheetData, setCurrentSheetData] = useState<any>(null);
  const { navigateToPage } = useMeritEngineNavigation();
  const [selectedMode, setSelectedMode] = useState<Mode>("Must_Run");
  const [selectedGenTypes, setSelectedGenTypes] = useState<any[]>([]);
  const [selectedOwnerships, setSelectedOwnerships] = useState<any[]>([]);
  const [selectedGeneratorName, setSelectedGeneratorName] = useState<any[]>([]);
  const [availRows, setAvailRows] = useState<AvailabilityRow[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  const selectedGenTypeValues = selectedGenTypes.map((o: any) => o.value);
  const selectedOwnershipValues = selectedOwnerships.map((o: any) => o.value);

  /* Reset cached sheet data whenever the active tab changes */
  useEffect(() => {
    setCurrentSheetData(null);
  }, [selectedSheet]);

  const handleDownload = async () => {
    const sheetLabel =
      SHEETS.find((s) => s.id === selectedSheet)?.label ?? selectedSheet;
    const filename = `${sheetLabel.replace(/[^a-zA-Z0-9]/g, "_")}_${preSelectedDate}.csv`;

    let rows: string[][] = [];
    const MLABELS = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    if (selectedSheet === "Availability_Detail") {
      if (!preSelectedDate || !modSelectedSession) return;
      try {
        const blob = await fetchAvailabilityDetail(preSelectedDate, modSelectedSession);
        saveAs(blob, `Availability_Detail_${preSelectedDate}.xlsx`);
      } catch (err) {
        console.error("Availability Detail download failed", err);
      }
      return;
    } else if (
      [
        "Demand_Forecast_Daywise",
        "Availability_Daywise",
        "Banking_Daywise",
        "Overhauling_Daywise",
        "Final_Availability_Daywise",
        "Shortage_Surplus_Daywise",
      ].includes(selectedSheet)
    ) {
      if (!currentSheetData) return;
      rows.push(["Time Slot", ...currentSheetData.dates]);
      currentSheetData.data.forEach((row: any) =>
        rows.push([
          row.time_slot,
          ...row.values.map((v: any) => (v === null ? "" : String(v))),
        ]),
      );
    } else if (selectedSheet === "Availability_Summary") {
      if (!currentSheetData) return;
      const months = (
        [
          ...new Set(
            currentSheetData.data.flatMap((r: any) =>
              r.months.map((m: any) => m.month),
            ),
          ),
        ] as number[]
      ).sort((a, b) => a - b);

      /* Two-row header: row 0 = month labels (merged across 5 sub-cols),
         row 1 = sub-column labels. "Time Slot" spans both rows via a merge. */
      const headerRow1: any[] = ["Time Slot"];
      const headerRow2: any[] = [""];
      const merges: any[] = [
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Time Slot spans 2 rows
      ];
      let colIdx = 1;
      months.forEach((m) => {
        headerRow1.push(MLABELS[m - 1], "", "", "", "");
        headerRow2.push("ISGS", "GNA", "REMC", "SGS", "Total");
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + 4 } });
        colIdx += 5;
      });

      const aoa: any[][] = [headerRow1, headerRow2];
      currentSheetData.data.forEach((row: any) => {
        const mm = new Map(row.months.map((m: any) => [m.month, m]));
        aoa.push([
          row.time_slot,
          ...months.flatMap((m) => {
            const md: any = mm.get(m);
            return ["isgs", "gna", "remc", "sgs", "total"].map((k) =>
              md ? md[k] : "",
            );
          }),
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!merges"] = merges;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Availability Summary");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        `Availability_Summary_${preSelectedDate}.xlsx`,
      );
      return;
    } else if (
      [
        "15Days_Report-Blockwise",
        "15Days_Report-Hourly",
        "Monthly_Report-Blockwise",
        "Monthly_Report-Hourly",
      ].includes(selectedSheet)
    ) {
      if (!currentSheetData) return;
      const fmtDate = (s: string) => {
        const d = new Date(s);
        const day = String(d.getDate()).padStart(2, "0");
        const mon = d.toLocaleDateString("en-GB", { month: "short" });
        return `${day}-${mon}-${d.getFullYear()}`;
      };
      const pHeaders = currentSheetData.periods.flatMap((p: any) => {
        const lbl = `${fmtDate(p.from)} - ${fmtDate(p.to)}`;
        return [
          "Demand",
          "Availability",
          "Banking",
          "Overhauling",
          "Final Avail.",
          "Surplus/Shortage",
        ].map((m) => `${lbl} ${m}`);
      });
      rows.push([
        currentSheetData.granularity === "block" ? "Time Slot" : "Hour",
        ...pHeaders,
      ]);
      currentSheetData.data.forEach((row: any) =>
        rows.push([
          row.time,
          ...row.periods.flatMap((pm: any) => [
            pm.demand === null ? "" : String(pm.demand),
            String(pm.availability),
            String(pm.banking),
            String(pm.overhauling),
            String(pm.final_availability),
            pm.surplus_shortage === null ? "" : String(pm.surplus_shortage),
          ]),
        ]),
      );
    } else if (selectedSheet === "Only_Shortage_Surplus_Hourly") {
      if (!currentSheetData) return;
      const keys = [
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
        "11",
        "12",
      ].filter((k) => currentSheetData.data.some((r: any) => k in r.months));
      rows.push(["Hour", ...keys.map((k) => MLABELS[parseInt(k) - 1])]);
      currentSheetData.data.forEach((row: any) =>
        rows.push([
          row.time,
          ...keys.map((k) => (k in row.months ? String(row.months[k]) : "")),
        ]),
      );
    }

    if (rows.length === 0) return;
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    saveAs(
      new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }),
      filename,
    );
  };

  /* Fetch availability data only after the session has been confirmed.
     The MeritEngineRouter dispatches both `date` and `session` in the
     same synchronous block after getModSession() resolves, so React 18
     batches them into a single re-render — this effect therefore fires
     exactly once per real date/session pair, never with the initial
     default date that exists in the store before the session loads.
     The cancellation flag discards any in-flight response if the date
     or session changes before it completes. */
  useEffect(() => {
    if (!preSelectedDate || !modSelectedSession) return;
    let cancelled = false;

    setDataLoading(true);
    getAvailabilityDetailSheet({ date: preSelectedDate })
      .then((res: any) => {
        if (cancelled) return;
        setAvailRows(res?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setAvailRows([]);
      })
      .finally(() => {
        if (!cancelled) setDataLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [preSelectedDate, modSelectedSession]);

  /* Derive unique generators from the API rows */
  /* Deduplicate by (generator_name, category) so a generator that
     appears in BOTH Must_Run and MOD rows is kept as two separate
     entries — one per mode. Without this, the first mode wins and
     the generator is invisible when the user switches to the other. */
  const apiGenerators = useMemo<GeneratorDef[]>(() => {
    const seen = new Set<string>();
    const gens: GeneratorDef[] = [];
    availRows.forEach((row) => {
      const key = row.generator_name + "|" + row.category;
      if (!seen.has(key)) {
        seen.add(key);
        gens.push({
          id: key /* unique per (name, mode) */,
          displayName: row.generator_name,
          shortName:
            row.generator_name.length > 22
              ? row.generator_name.substring(0, 20) + "…"
              : row.generator_name,
          ownership: row.ownership,
          genType: row.source as GenType,
          mode: row.category,
        });
      }
    });
    /* ── DEBUG: log what was built ── */
    const byMode: Record<string, string[]> = {};
    gens.forEach((g) => {
      if (!byMode[g.mode]) byMode[g.mode] = [];
      byMode[g.mode].push(g.displayName);
    });
    console.group(
      "%c[apiGenerators] Derived from availRows",
      "color:#34656D;font-weight:bold",
    );
    console.log("Total entries:", gens.length);
    Object.entries(byMode).forEach(([mode, names]) =>
      console.log(mode + " →", names.length, "generators:", names),
    );
    console.groupEnd();
    /* ─────────────────────────────── */

    return gens;
  }, [availRows]);

  /* Build lookup: generatorName → timeBlock → MW value */
  /* Same composite key as apiGenerators so dataMap.get(gen.id) always
     returns data for the right (generator, mode) combination. */
  const dataMap = useMemo<Map<string, Map<number, number | null>>>(() => {
    const map = new Map<string, Map<number, number | null>>();
    availRows.forEach((row) => {
      const key = row.generator_name + "|" + row.category;
      if (!map.has(key)) map.set(key, new Map());
      map.get(key)!.set(row.time_block, row.value); /* value may be null */
    });
    return map;
  }, [availRows]);

  /* Generator name options — scoped to current mode + active filters */
  const filteredGeneratorNameOptions = apiGenerators
    .filter(
      (g) =>
        g.mode === selectedMode &&
        (selectedOwnershipValues.length === 0 ||
          selectedOwnershipValues.includes(g.ownership)) &&
        (selectedGenTypeValues.length === 0 ||
          selectedGenTypeValues.includes(g.genType)),
    )
    .map((g) => ({ label: g.displayName, value: g.id }));

  /* ── Filter dropdown options derived from the API response ──────── */

  /* Mode/category options — unique categories present in the data */
  const categoryOptions = useMemo(
    () =>
      [...new Set(availRows.map((r) => r.category))].map((c) => ({
        label: c === "Must_Run" ? "Must Run" : c,
        value: c,
      })),
    [availRows],
  );

  /* Ownership options — unique ownerships for the selected mode */
  const ownershipOptions = useMemo(
    () =>
      [
        ...new Set(
          apiGenerators
            .filter((g) => g.mode === selectedMode)
            .map((g) => g.ownership),
        ),
      ].map((o) => ({ label: o, value: o })),
    [apiGenerators, selectedMode],
  );

  /* Source/type options — unique types for the selected mode + ownership */
  const sourceOptions = useMemo(
    () =>
      [
        ...new Set(
          apiGenerators
            .filter(
              (g) =>
                g.mode === selectedMode &&
                (selectedOwnershipValues.length === 0 ||
                  selectedOwnershipValues.includes(g.ownership)),
            )
            .map((g) => g.genType),
        ),
      ].map((t) => ({ label: t, value: t })),
    [apiGenerators, selectedMode, selectedOwnershipValues],
  );

  const standardFileTypes = [
    "Net Schedule ISGS",
    "Net Schedule OA GNA",
    "Net Schedule OA REMC",
    "VC for Generators (Optional)",
  ];

  const FileTypeBackendAlias: { [key: string]: string } = {
    "Net Schedule ISGS": "isgs",
    "OPGC-1 (IBTPS Unit -1 & 2)": "opgc-1",
    "OPGC-2 (IBTPS Unit -3 & 4)": "opgc-2",
    Vedanta: "vedanta",
    GMR: "gmr",
    JIPL: "jipl",
    JSWEUL: "jsweul",
    NBVL: "nbvl",
    "Maa Durga": "maa-durga",
    "Net Schedule OA REMC": "oa-remc",
    "Net Schedule OA GNA": "oa-gna",
    "State Hydro Generation": "state-hydro",
    "Intra State RE": "state-re",
    "CGP Injection": "cgp",
    Buffer: "buffer",
    Banking: "banking",
    "Short Term Buy/Sell": "short-term-buy-sell",
    "VC for Generators (Optional)": "vc-generator",
    "RTM day before": "rtm-day-before",
    "RTM day current": "rtm-day-current",
    "DAM day current": "dam-day-current",
    "Net Schedule ISGS Template": "isgs-temp",
    "OPGC-1 (IBTPS Unit -1 & 2) Template": "opgc-1-temp",
    "OPGC-2 (IBTPS Unit -3 & 4) Template": "opgc-2-temp",
    "Vedanta Template": "vedanta-temp",
    "GMR Template": "gmr-temp",
    "JIPL Template": "jipl-temp",
    "JSWEUL Template": "jsweul-temp",
    "NBVL Template": "nbvl-temp",
    "Maa Durga Template": "maa-durga-temp",
    "Net Schedule OA REMC Template": "oa-remc-temp",
    "Net Schedule OA GNA Template": "oa-gna-temp",
    "State Hydro Generation Template": "state-hydro-temp",
  };

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [fileErrors, setFileErrors] = useState<Record<string, string | null>>(
    {},
  );

  const getStandardFileName = (fileType: string): string => {
    if (standardFileTypes.includes(fileType)) {
      return fileType;
    }
    return TemplateToStandardMap[fileType] || fileType;
  };

  const getUploadedFileForStandardType = (
    standardFileName: string,
  ): { file: File | null; isTemplate: boolean } => {
    const standardFile = uploadedFiles.find(
      (f) => f.type === standardFileName && !f.isTemplate,
    );
    if (standardFile) return { file: standardFile.file, isTemplate: false };

    const templateFile = uploadedFiles.find((f) => {
      const standardName = getStandardFileName(f.type);
      return standardName === standardFileName && f.isTemplate;
    });
    if (templateFile) return { file: templateFile.file, isTemplate: true };

    return { file: null, isTemplate: false };
  };

  function detectFileType(fileName: string): {
    type: string;
    isTemplate: boolean;
  } {
    for (const [regexKey, pattern] of Object.entries(FileRegexPatterns)) {
      if (pattern.test(fileName)) {
        const mappedType = RegexToFileTypeMap[regexKey];
        if (mappedType) {
          const isTemplate = mappedType.includes("Template");
          return { type: mappedType, isTemplate };
        }
      }
    }
    return { type: "", isTemplate: false };
  }

  const handleFileChange = (
    fileName: string,
    file: File | null,
    isTemplate: boolean,
  ) => {
    if (!file) {
      setUploadedFiles((prevFiles) => {
        const standardFileName = getStandardFileName(fileName);

        return prevFiles.filter((f) => {
          const fStandardName = getStandardFileName(f.type);
          return fStandardName !== standardFileName;
        });
      });

      setFileErrors((prevErrors) => {
        if (!prevErrors[fileName]) {
          return {
            ...prevErrors,
            [fileName]: null,
          };
        }
        return prevErrors;
      });
      return;
    }

    const standardFileName = getStandardFileName(fileName);

    setUploadedFiles((prevFiles) => {
      const filtered = prevFiles.filter((f) => {
        const fStandardName = getStandardFileName(f.type);
        return fStandardName !== standardFileName;
      });

      return [
        ...filtered,
        {
          type: fileName,
          date: preSelectedDate,
          file_url: URL.createObjectURL(file),
          file,
          id: Date.now(),
          isTemplate: isTemplate,
        },
      ];
    });

    setFileErrors((prevErrors) => ({
      ...prevErrors,
      [fileName]: null,
    }));
  };

  const handleFileError = (fileName: string, error: string | null) => {
    setFileErrors((prevErrors) => ({
      ...prevErrors,
      [fileName]: error,
    }));
  };

  const [selectedFileType, setSelectedFileType] = useState<string>("All Files");

  const filesToDisplay =
    selectedFileType === "All Files"
      ? standardFileTypes
      : standardFileTypes.filter((fileType) => fileType === selectedFileType);

  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const tabScrollRef = useRef<HTMLDivElement>(null);

  const handleTabScrollLeft = () => {
    const idx = SHEETS.findIndex((s) => s.id === selectedSheet);
    if (idx > 0) {
      setSelectedSheet(SHEETS[idx - 1].id);
      tabScrollRef.current?.scrollBy({ left: -150, behavior: "smooth" });
    }
  };

  const handleTabScrollRight = () => {
    const idx = SHEETS.findIndex((s) => s.id === selectedSheet);
    if (idx < SHEETS.length - 1) {
      setSelectedSheet(SHEETS[idx + 1].id);
      tabScrollRef.current?.scrollBy({ left: 150, behavior: "smooth" });
    }
  };

  const handleBulkUploadClick = () => {
    bulkFileInputRef.current?.click();
  };

  function handleBulkUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!preSelectedDate) {
      setError("Please select a date");
      event.target.value = "";
      return;
    }

    if (!files || files.length === 0) {
      setError("Please select files");
      return;
    }

    const validExtensions = [".xlsx", ".xls", ".csv", ".pdf"];
    const newFiles: UploadedFile[] = [];
    const undetectedFiles: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError(
          `Invalid file format for ${file.name}. Please select only XLSX, XLS or PDF files.`,
        );
        event.target.value = "";
        return;
      }

      const { type: detectedType, isTemplate } = detectFileType(file.name);

      if (!detectedType) {
        undetectedFiles.push(file.name);
        continue;
      }

      newFiles.push({
        type: detectedType,
        date: preSelectedDate,
        file_url: URL.createObjectURL(file),
        file,
        id: Date.now() + i,
        isTemplate: isTemplate,
      });
    }

    if (undetectedFiles.length > 0) {
      setError(
        `Could not auto-detect file type for:\n${undetectedFiles.join(
          "\n",
        )}\n\nPlease upload these files individually.`,
      );
    }

    if (newFiles.length > 0) {
      setUploadedFiles((prev) => {
        const affectedStandardTypes = new Set<string>();
        newFiles.forEach((file) => {
          const standardName = getStandardFileName(file.type);
          affectedStandardTypes.add(standardName);
        });

        prev.forEach((f) => {
          const standardName = getStandardFileName(f.type);
          if (affectedStandardTypes.has(standardName)) {
            URL.revokeObjectURL(f.file_url);
          }
        });

        const kept = prev.filter((f) => {
          const standardName = getStandardFileName(f.type);
          return !affectedStandardTypes.has(standardName);
        });

        return [...kept, ...newFiles];
      });

      setFileErrors((prev) => {
        const updated = { ...prev };
        newFiles.forEach((f) => {
          const standardName = getStandardFileName(f.type);
          updated[standardName] = null;
        });
        return updated;
      });
    }

    event.target.value = "";
  }

  const GotoModTable = async () => {
    // Get all required files (excluding VC for Generators)
    const requiredFileTypes = standardFileTypes.filter(
      (type) => type !== "VC for Generators (Optional)",
    );

    // Check if VC file is uploaded
    const vcFile = getUploadedFileForStandardType(
      "VC for Generators (Optional)",
    );
    const isVcUploaded = vcFile.file !== null;

    // Get uploaded required files
    const uploadedRequiredTypes = requiredFileTypes.filter((standardType) => {
      const { file } = getUploadedFileForStandardType(standardType);
      return file !== null;
    });

    const requiredCount = requiredFileTypes.length;

    if (isVcUploaded) {
      const totalUploaded = uploadedRequiredTypes.length + 1;
      if (totalUploaded !== requiredCount + 1) {
        setError(
          `VC file is uploaded. Please upload all remaining files. Currently uploaded: ${totalUploaded}/${
            requiredCount + 1
          }`,
        );
        return;
      }
    } else {
      if (uploadedRequiredTypes.length !== requiredCount) {
        setError(
          `Please upload all required files. Currently uploaded: ${uploadedRequiredTypes.length}/${requiredCount} (VC file is optional)`,
        );
        return;
      }
    }

    try {
      setLoading(true);
      const formData = new FormData();

      const metaData = uploadedFiles.map((file, idx) => {
        const field = `file_${idx}`;
        formData.append(field, file.file);

        let backendType: string;
        if (file.isTemplate) {
          const standardName = getStandardFileName(file.type);
          const baseAlias = FileTypeBackendAlias[standardName];
          backendType = baseAlias ? `${baseAlias}-temp` : file.type;
        } else {
          backendType = FileTypeBackendAlias[file.type] || file.type;
        }

        return {
          field,
          date: file.date,
          type: backendType,
        };
      });

      formData.append("metaData", JSON.stringify(metaData));
      formData.append("date", preSelectedDate);
      formData.append("session", modSelectedSession);
      const res = await ModBulkUploadFile(formData);
      setUploadedFiles([]);
      setSelectedFileType("All Files");

      if (res) {
        navigateToPage("merit-order-dispatch");
        (window as any).resetNextTabs?.("GENERATION");
      }
    } catch (error) {
      console.error("Error generating file:", error);
      setError("Error generating file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Count uploaded files
  const uploadedRequiredCount = standardFileTypes
    .filter((type) => type !== "VC for Generators (Optional)")
    .filter((standardType) => {
      const { file } = getUploadedFileForStandardType(standardType);
      return file !== null;
    }).length;

  const vcFile = getUploadedFileForStandardType("VC for Generators (Optional)");
  const isVcUploaded = vcFile.file !== null;

  const requiredFileCount = standardFileTypes.filter(
    (type) => type !== "VC for Generators (Optional)",
  ).length;

  const isButtonEnabled = uploadedRequiredCount === requiredFileCount;

  return (
    <div className="flex flex-col w-full">
      <MainHeader />
      <div className="tabContent">
        <div className="h-[calc(-73px+100vh)] p-4">
          <div className="w-full h-full flex flex-col gap-4 min-h-0">
            <ModHeader title="Available Generation Capacity" />
            <div className="gas-chart-container flex flex-col flex-1 min-h-0">
              {/* Horizontally scrollable sheet tab bar with arrow controls inside */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                {/* Left arrow — overlaid inside the bar */}
                <button
                  onClick={handleTabScrollLeft}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 2,
                    backgroundColor: "#EFFCFF",
                    border: "0.5px solid #34656D",
                    borderRadius: "8px 0 0 8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    color: "#34656D",
                  }}
                >
                  <ChevronLeft size={16} />
                </button>

                <div
                  ref={tabScrollRef}
                  style={{
                    backgroundColor: "#EFFCFF",
                    border: "0.5px solid #34656D",
                    borderRadius: 8,
                    padding: "6px 36px",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#34656D #c8eef3",
                  }}
                >
                  {SHEETS.map((sheet) => {
                    const isActive = selectedSheet === sheet.id;
                    return (
                      <button
                        key={sheet.id}
                        onClick={() => setSelectedSheet(sheet.id)}
                        style={{
                          display: "inline-block",
                          padding: "7px 16px",
                          marginRight: 4,
                          borderRadius: 6,
                          border: "none",
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: isActive ? 500 : 400,
                          whiteSpace: "nowrap",
                          transition: "background-color 0.15s, color 0.15s",
                          backgroundColor: isActive ? "#34656D" : "transparent",
                          color: isActive ? "#ffffff" : "#34656D",
                        }}
                      >
                        {sheet.label}
                      </button>
                    );
                  })}
                </div>

                {/* Right arrow — overlaid inside the bar */}
                <button
                  onClick={handleTabScrollRight}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 2,
                    backgroundColor: "#EFFCFF",
                    border: "0.5px solid #34656D",
                    borderRadius: "0 8px 8px 0",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    color: "#34656D",
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* ── Download button row (all tabs except Availability_Detail) ── */}
              {selectedSheet !== "Availability_Detail" && (
                <div className="flex justify-end py-1.5 flex-shrink-0">
                  <button
                    onClick={handleDownload}
                    disabled={!currentSheetData}
                    className="flex items-center justify-center gap-2 px-4 h-[30px] text-xs rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-all bg-[#FFFAF6] text-[#F1935C] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Data</span>
                  </button>
                </div>
              )}

              {/* ── Shape A — Daywise sheets (96 blocks × N dates) ── */}
              {selectedSheet === "Demand_Forecast_Daywise" && (
                <DaywiseSheetTable
                  date={preSelectedDate}
                  fetchFn={getDemandForecastDaywiseSheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}
              {selectedSheet === "Availability_Daywise" && (
                <DaywiseSheetTable
                  date={preSelectedDate}
                  fetchFn={getAvailabilityDaywiseSheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}
              {selectedSheet === "Banking_Daywise" && (
                <DaywiseSheetTable
                  date={preSelectedDate}
                  fetchFn={getBankingDaywiseSheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}
              {selectedSheet === "Overhauling_Daywise" && (
                <DaywiseSheetTable
                  date={preSelectedDate}
                  fetchFn={getOverhaulingDaywiseSheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}
              {selectedSheet === "Final_Availability_Daywise" && (
                <DaywiseSheetTable
                  date={preSelectedDate}
                  fetchFn={getFinalAvailabilityDaywiseSheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}
              {selectedSheet === "Shortage_Surplus_Daywise" && (
                <DaywiseSheetTable
                  date={preSelectedDate}
                  fetchFn={getShortageSurplusDaywiseSheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}

              {/* ── Shape C — Availability Summary (96 blocks × 12 months × 5 sources) */}
              {selectedSheet === "Availability_Summary" && (
                <AvailabilitySummaryTable
                  date={preSelectedDate}
                  fetchFn={getAvailabilitySummarySheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}

              {/* ── Shape B — Period reports (96 blocks or 24 hours × 6 metrics × N periods) */}
              {selectedSheet === "15Days_Report-Blockwise" && (
                <PeriodReportTable
                  date={preSelectedDate}
                  fetchFn={get15DaysReportBlockwiseSheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}
              {selectedSheet === "15Days_Report-Hourly" && (
                <PeriodReportTable
                  date={preSelectedDate}
                  fetchFn={get15DaysReportHourlySheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}
              {selectedSheet === "Monthly_Report-Blockwise" && (
                <PeriodReportTable
                  date={preSelectedDate}
                  fetchFn={getMonthlyReportBlockwiseSheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}
              {selectedSheet === "Monthly_Report-Hourly" && (
                <PeriodReportTable
                  date={preSelectedDate}
                  fetchFn={getMonthlyReportHourlySheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}

              {/* ── Shape D — Shortage/Surplus Hourly (24 hours × 12 months) */}
              {selectedSheet === "Only_Shortage_Surplus_Hourly" && (
                <ShortageSurplusHourlyTable
                  date={preSelectedDate}
                  fetchFn={getOnlyShortageSurplusHourlySheet}
                  onDataLoaded={setCurrentSheetData}
                />
              )}

              {/* ── Availability Detail tab content ─────────────────── */}
              {selectedSheet === "Availability_Detail" && (
                <>
                  {/* Filter bar */}
                  <div className="flex items-center gap-3 flex-wrap pt-2 flex-shrink-0">
                    {/* Mode toggle */}
                    <div
                      className="flex rounded-lg overflow-hidden"
                      style={{ border: "0.5px solid #34656D" }}
                    >
                      {(["Must_Run", "MOD"] as Mode[]).map((mode) => (
                        <button
                          key={mode}
                          onClick={() => {
                            setSelectedMode(mode);
                            setSelectedOwnerships([]);
                            setSelectedGenTypes([]);
                            setSelectedGeneratorName([]);
                          }}
                          style={{
                            padding: "5px 16px",
                            fontSize: 12,
                            fontWeight: selectedMode === mode ? 600 : 400,
                            backgroundColor:
                              selectedMode === mode ? "#34656D" : "#EFFCFF",
                            color: selectedMode === mode ? "#fff" : "#34656D",
                            border: "none",
                            cursor: "pointer",
                            transition: "background-color 0.15s",
                          }}
                        >
                          {mode === "Must_Run" ? "Must Run" : "MOD"}
                        </button>
                      ))}
                    </div>

                    <SearchBox
                      options={ownershipOptions}
                      selectedOptions={selectedOwnerships}
                      onChange={(sel) => {
                        setSelectedOwnerships(sel as any[]);
                        setSelectedGeneratorName([]);
                      }}
                      isMany={true}
                      placeholder="Ownership"
                      minWidth={140}
                      isClearable
                    />

                    <SearchBox
                      options={sourceOptions}
                      selectedOptions={selectedGenTypes}
                      onChange={(sel) => {
                        setSelectedGenTypes(sel as any[]);
                        setSelectedGeneratorName([]);
                      }}
                      isMany={true}
                      placeholder="Gen Type"
                      minWidth={140}
                      isClearable
                    />

                    <SearchBox
                      options={filteredGeneratorNameOptions}
                      selectedOptions={selectedGeneratorName}
                      onChange={(sel) => setSelectedGeneratorName(sel as any[])}
                      isMany={true}
                      placeholder="Generator"
                      minWidth={160}
                      isClearable
                    />

                    {/* Download button — inline with filters */}
                    <button
                      onClick={handleDownload}
                      disabled={availRows.length === 0}
                      className="ml-auto flex items-center justify-center gap-2 px-4 h-[30px] text-xs rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-all bg-[#FFFAF6] text-[#F1935C] disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Data</span>
                    </button>
                  </div>

                  {/* Availability table */}
                  <AvailabilityTable
                    selectedMode={selectedMode}
                    selectedOwnerships={selectedOwnershipValues}
                    selectedGenTypes={selectedGenTypeValues}
                    selectedGeneratorIds={selectedGeneratorName.map(
                      (g: any) => g.value,
                    )}
                    generators={apiGenerators}
                    dataMap={dataMap}
                    loading={dataLoading}
                  />
                </>
              )}
            </div>
            <div className="gna-footer-container">
              <div className="grid grid-cols-3 justify-between gap-5 ">
                <Tooltip
                  note={
                    <p>
                      Default values and variable charges auto-fetched from
                      system.
                    </p>
                  }
                  text=" "
                >
                  <p className="gna-footer-text">Source</p>
                </Tooltip>
                <p className="gna-footer-text col-span-1 text-center">
                  Powered by <strong>GNA Energy</strong>
                </p>
                <strong className="gna-footer-text col-span-1 text-end">
                  Beta Version
                </strong>
              </div>
            </div>{" "}
          </div>
        </div>
      </div>
    </div>
  );
}
